/**
 * Lump Sum Engine — Applies one-time principal reduction events
 * to both policy loan and standard loan scenarios.
 */

import type { LumpSumScenario } from "@/components/LumpSumScenarios";
import type { RecurringLumpSumConfig } from "@/components/LumpSumScenarios";
import {
  type PolicyLoanPayment,
  type StandardLoanAmortizationRow,
  type StandardLoanResult,
  type StandardLoanInput,
  getPaymentsPerYear,
  getNextPaymentDate,
  calculateStandardLoanPayment,
  compareDateStr,
  addMonths,
} from "@/lib/calculationEngine";

export interface LumpSumPaymentEvent {
  date: string;
  amount: number;
  source: string;
}

/**
 * Resolve lump sum scenarios into dated payment events.
 */
export function resolveLumpSumDates(
  scenarios: LumpSumScenario[],
  loanStartDate: string
): LumpSumPaymentEvent[] {
  return scenarios
    .filter(s => s.amount > 0 && s.knowsDate !== null)
    .map(s => {
      let date: string;
      if (s.knowsDate && s.date) {
        date = s.date;
      } else {
        date = addMonths(loanStartDate, s.estimatedMonthOffset);
      }
      return { date, amount: s.amount, source: s.source };
    })
    .sort((a, b) => compareDateStr(a.date, b.date));
}

// ---------------------------------------------------------------------------
// Recurring lump sums
// ---------------------------------------------------------------------------

/** Convert a recurring frequency into the number of months between events. */
export function recurringMonthStep(cfg: RecurringLumpSumConfig): number {
  switch (cfg.frequency) {
    case 'monthly':     return 1;
    case 'quarterly':   return 3;
    case 'semiannual':  return 6;
    case 'annual':      return 12;
    case 'custom':      return Math.max(1, Math.round(cfg.customMonths || 1));
  }
}

/** Hard cap so misconfigured inputs cannot generate unbounded schedules. */
const RECURRING_MAX_EVENTS = 600; // ≥ 50 years monthly

/**
 * Expand a recurring lump sum configuration into discrete dated events.
 * Stops at the first of: configured occurrences, configured end date, or the safety cap.
 */
export function expandRecurringLumpSums(
  cfg: RecurringLumpSumConfig | null | undefined,
  loanStartDate: string,
): LumpSumPaymentEvent[] {
  if (!cfg || !cfg.enabled) return [];
  if (!(cfg.amount > 0)) return [];

  // Resolve first event date
  let date: string;
  if (cfg.startMode === 'date' && cfg.startDate) {
    date = cfg.startDate;
  } else {
    const offset = Math.max(0, Math.round(cfg.startMonthOffset || 0));
    date = addMonths(loanStartDate, offset);
  }

  const step = recurringMonthStep(cfg);

  const maxByOccurrences = cfg.endMode === 'occurrences'
    ? Math.max(0, Math.min(RECURRING_MAX_EVENTS, Math.round(cfg.occurrences || 0)))
    : RECURRING_MAX_EVENTS;
  const endDate = cfg.endMode === 'date' ? (cfg.endDate || '') : '';

  const events: LumpSumPaymentEvent[] = [];
  for (let i = 0; i < maxByOccurrences; i++) {
    if (endDate && compareDateStr(date, endDate) > 0) break;
    events.push({ date, amount: cfg.amount, source: `recurring:${cfg.source}` });
    date = addMonths(date, step);
  }
  return events;
}

/**
 * Combine one-time scenarios and a recurring config into a single sorted event list.
 */
export function buildAllLumpSumEvents(
  scenarios: LumpSumScenario[],
  recurring: RecurringLumpSumConfig | null | undefined,
  loanStartDate: string,
): LumpSumPaymentEvent[] {
  const oneTime = resolveLumpSumDates(scenarios, loanStartDate);
  const recurringEvents = expandRecurringLumpSums(recurring, loanStartDate);
  return [...oneTime, ...recurringEvents].sort((a, b) => compareDateStr(a.date, b.date));
}

/**
 * Inject lump sum payments into the planned payment schedule for the policy loan.
 */
export function injectLumpSumsIntoPayments(
  plannedPayments: PolicyLoanPayment[],
  lumpSums: LumpSumPaymentEvent[]
): PolicyLoanPayment[] {
  const combined = [
    ...plannedPayments,
    ...lumpSums.map((ls, i) => ({
      id: `lumpsum-${i}`,
      date: ls.date,
      amount: ls.amount,
      source: 'planned' as const,
    })),
  ];
  return combined.sort((a, b) => compareDateStr(a.date, b.date));
}

/**
 * Build a standard loan amortization that includes lump sum payments.
 * Lump sums reduce principal but don't change the recurring payment amount.
 */
export function buildStandardLoanWithLumpSums(
  input: StandardLoanInput,
  lumpSums: LumpSumPaymentEvent[]
): StandardLoanResult {
  const paymentsPerYear = getPaymentsPerYear(input.paymentFrequency);
  const periodicRate = input.annualRate / paymentsPerYear;
  const payment = calculateStandardLoanPayment(input);

  let balance = input.principal;
  let paymentDate = input.startDate;
  let totalInterest = 0;
  const schedule: StandardLoanAmortizationRow[] = [];
  
  // Track which lump sums have been applied
  const appliedLumpSums = new Set<number>();

  for (let i = 1; i <= input.termCount; i++) {
    // Apply any lump sums that fall on or before this payment date
    for (let li = 0; li < lumpSums.length; li++) {
      if (!appliedLumpSums.has(li) && compareDateStr(lumpSums[li].date, paymentDate) <= 0) {
        balance = Math.max(0, balance - lumpSums[li].amount);
        appliedLumpSums.add(li);
      }
    }

    if (balance <= 0.000001) {
      break;
    }

    const interestPortion = balance * periodicRate;
    let principalPortion = payment - interestPortion;
    let actualPayment = payment;

    if (principalPortion > balance) {
      principalPortion = balance;
      actualPayment = interestPortion + principalPortion;
    }

    balance = Math.max(0, balance - principalPortion);
    totalInterest += interestPortion;

    schedule.push({
      paymentNumber: i,
      paymentDate,
      paymentAmount: actualPayment,
      interestPortion,
      principalPortion,
      remainingBalance: balance,
    });

    if (balance <= 0.000001) break;
    paymentDate = getNextPaymentDate(paymentDate, input.paymentFrequency);
  }

  return {
    periodicRate,
    paymentsPerYear,
    scheduledPayment: payment,
    totalPayments: schedule.reduce((sum, row) => sum + row.paymentAmount, 0),
    totalInterest,
    payoffDate: schedule[schedule.length - 1]?.paymentDate ?? input.startDate,
    amortizationSchedule: schedule,
  };
}

/**
 * Calculate redirected system funding after early payoff.
 */
export interface RedirectedFundingSummary {
  originalPayoffDate: string;
  updatedPayoffDate: string;
  originalPaymentCount: number;
  updatedPaymentCount: number;
  remainingPaymentsAfterPayoff: number;
  paymentAmount: number;
  totalRedirectedAmount: number;
  originalFinancingMargin: number;
  updatedFinancingMargin: number;
}

export function calculateRedirectedFunding(params: {
  originalPaymentCount: number;
  updatedPaymentCount: number;
  paymentAmount: number;
  originalPayoffDate: string;
  updatedPayoffDate: string;
  originalFinancingMargin: number;
  updatedFinancingMargin: number;
}): RedirectedFundingSummary {
  const remaining = Math.max(0, params.originalPaymentCount - params.updatedPaymentCount);
  return {
    originalPayoffDate: params.originalPayoffDate,
    updatedPayoffDate: params.updatedPayoffDate,
    originalPaymentCount: params.originalPaymentCount,
    updatedPaymentCount: params.updatedPaymentCount,
    remainingPaymentsAfterPayoff: remaining,
    paymentAmount: params.paymentAmount,
    totalRedirectedAmount: remaining * params.paymentAmount,
    originalFinancingMargin: params.originalFinancingMargin,
    updatedFinancingMargin: params.updatedFinancingMargin,
  };
}
